<template>
  <section id="pos-id-manual-fix-mappings" class="pos-id-page">
    <b-card>
      <div class="title d-flex justify-content-between align-items-center flex-wrap">
        <p class="mb-1">pos-id 重定向映射</p>
        <div class="d-flex flex-wrap mb-1">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button"
            @click="goManagementCenter"
          >
            回目錄
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button ml-1"
            @click="goDeleteAudits"
          >
            審查記錄（刪除）
          </b-button>
        </div>
      </div>

      <b-alert v-if="readOnly" show variant="warning" class="mb-3">
        {{ readOnlyHint }}
      </b-alert>
      <b-alert
        v-if="errorText"
        show
        variant="danger"
        dismissible
        class="mb-3"
        @dismissed="errorText = ''"
      >
        {{ errorText }}
      </b-alert>
      <b-alert show variant="info" class="mb-3">
        刪除後，後續 XML 若繼續發舊號將不再按映射解析（歷史票房不受影響）。
      </b-alert>

      <div class="pos-id-filters">
        <b-row>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">院線（可多選）</label>
            <el-select
              v-model="filters.from_circuit_id"
              multiple
              filterable
              collapse-tags
              placeholder="請選擇…"
              style="width: 100%"
            >
              <el-option
                v-for="item in circuitOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">舊號 from_pos_id（前綴 / 精確）</label>
            <div class="d-flex">
              <b-form-input
                v-model="filters.from_pos_id"
                placeholder="例：ED0011248"
                @keyup.enter.native="handleSearch"
              />
              <b-form-select
                v-model="filters.from_pos_id_mode"
                :options="posIdModeOptions"
                class="ml-1"
                style="width: 96px; flex: 0 0 96px"
              />
            </div>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">新號 to_pos_id（前綴 / 精確）</label>
            <div class="d-flex">
              <b-form-input
                v-model="filters.to_pos_id"
                placeholder="例：ED0011248A"
                @keyup.enter.native="handleSearch"
              />
              <b-form-select
                v-model="filters.to_pos_id_mode"
                :options="posIdModeOptions"
                class="ml-1"
                style="width: 96px; flex: 0 0 96px"
              />
            </div>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">標題前綴 from_title_name</label>
            <b-form-input
              v-model="filters.from_title_name"
              @keyup.enter.native="handleSearch"
            />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">建立日期（起，含當日）</label>
            <b-form-input v-model="filters.date_start" type="date" />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">建立日期（迄，含當日）</label>
            <b-form-input v-model="filters.date_end" type="date" />
          </b-col>
        </b-row>

        <div class="pos-id-filters__actions">
          <b-button
            size="sm"
            variant="primary"
            class="mr-1"
            :disabled="loading"
            @click="handleSearch"
          >
            查詢
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="handleReset"
          >
            重置
          </b-button>
        </div>
      </div>

      <div class="pos-id-table-bar">
        <span class="pos-id-table-bar__title">重定向映射</span>
        <span class="pos-id-table-bar__hint">
          點擊帶 <i class="el-icon-sort"></i> 的表頭可切換排序（↑ 升序 / ↓ 降序）
        </span>
      </div>

      <div v-loading="loading">
        <b-table
          bordered
          striped
          hover
          responsive
          show-empty
          empty-text="沒有符合條件的重定向映射"
          :items="rows"
          :fields="displayFields"
        >
          <template #head()="data">
            <pos-id-sortable-header
              :label="data.field.label"
              :sort-key="data.field.sortKey"
              :active-sort-key="filters.sort"
              :direct="filters.direct"
              @sort="handleHeaderSort"
            />
          </template>
          <template #cell(from_circuit_id)="row">
            <span class="text-nowrap">
              {{ textOrDash(row.item.from_circuit_id) }}
            </span>
          </template>
          <template #cell(from_pos_id)="row">
            <div class="text-nowrap">
              {{ textOrDash(row.item.from_pos_id) }}
              <div>
              <b-badge
                v-if="fromLedgerAlive(row.item)"
                variant="warning"
                title="舊號台帳仍存在（可能是雙向映射），刪除前請確認哪一邊才是目標"
              >
                舊記錄仍在
              </b-badge>
              </div>
            </div>
          </template>
          <template #cell(from_title_name)="row">
            <div>{{ textOrDash(row.item.from_title_name) }}</div>
          </template>
          <template #cell(engagements)="row">
            <div>
              <span :class="engagementClass(row.item)">
                {{ engagementCountText(row.item) }}
              </span>
              <div v-if="hasEngagement(row.item)" class="small text-muted">
                ${{ formatNumber(engagementOf(row.item).gross) }} /
                {{ formatNumber(engagementOf(row.item).admissions) }} 人次
              </div>
            </div>
          </template>
          <template #cell(to_record)="row">
            <span v-if="deadMapping(row.item)" class="text-danger font-weight-bold">
              已不存在（死規則）
            </span>
            <span v-else class="text-nowrap">{{ toRecordPosId(row.item) }}</span>
          </template>
          <template #cell(created_at)="row">
            <span class="text-nowrap">{{ textOrDash(row.item.created_at) }}</span>
          </template>
          <template #cell(actions)="row">
            <div class="d-flex flex-nowrap">
              <b-button size="sm" variant="outline-info" @click="openDetail(row.item)">
                詳情
              </b-button>
              <b-button
                v-if="!readOnly"
                size="sm"
                variant="outline-danger"
                class="ml-1"
                @click="openDelete(row.item)"
              >
                刪除
              </b-button>
            </div>
          </template>
        </b-table>

        <pos-id-pagination
          :page="filters.page"
          :count="filters.count"
          :total="total"
          @page-change="handlePageChange"
          @count-change="handleCountChange"
        />
      </div>
    </b-card>

    <mapping-detail-drawer
      :visible.sync="detailVisible"
      :record-id="detailId"
      @missing="onMissing"
      @read-only="setReadOnly"
    />

    <mapping-delete-dialog
      :visible.sync="deleteVisible"
      :record="deleteRecord"
      @deleted="onDeleted"
      @missing="onMissing"
      @read-only="setReadOnly"
    />
  </section>
</template>

<script>
import { Message } from 'element-ui'
import { getCircuitList } from '@/api/circuit'
import { getManualFixMappings } from '@/api/posId'
import {
  POS_ID_READ_ONLY_HINT,
  buildManualFixMappingQuery,
  formatNumber,
  hasFromLedger,
  isDeadMapping,
  mappingEngagementSummary,
  resolvePosIdError
} from '@/utils/posId'
import MappingDetailDrawer from './components/MappingDetailDrawer.vue'
import MappingDeleteDialog from './components/MappingDeleteDialog.vue'
import PosIdPagination from './components/PosIdPagination.vue'
import PosIdSortableHeader from './components/PosIdSortableHeader.vue'

const POS_ID_MODE_OPTIONS = [
  { text: '前綴', value: 'prefix' },
  { text: '精確', value: 'exact' }
]

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function textOrDash(value) {
  return hasText(value) ? String(value) : '—'
}

/** 列表 / 詳情的 payload 都是 {result,total,data:[]}（院線清單是 {result,data:[]}），兩種都兜底 */
function extractListPayload(res) {
  if (Array.isArray(res)) {
    return res
  }
  if (!res) {
    return []
  }
  if (Array.isArray(res.data)) {
    return res.data
  }
  if (res.data && Array.isArray(res.data.data)) {
    return res.data.data
  }
  return []
}

function createDefaultFilters() {
  return {
    from_circuit_id: [],
    from_pos_id: '',
    from_pos_id_mode: 'prefix',
    to_pos_id: '',
    to_pos_id_mode: 'prefix',
    from_title_name: '',
    date_start: '',
    date_end: '',
    sort: 'created_at',
    direct: 'desc',
    page: 1,
    count: 20
  }
}

/**
 * P6 重定向映射列表頁（guide §5.1 / §5.4 / §6 P6）。
 *
 * 鐵律：
 *  - 查詢參數一律走 buildManualFixMappingQuery（清空搜尋框不送空 key，guide §7.3-1）；
 *  - 「舊號是否仍有票房 / 新號台帳是否還在」一律用 mappingEngagementSummary /
 *    isDeadMapping / hasFromLedger 判斷，不在頁面重寫邏輯；
 *  - 本頁只有「查看 + 刪除」：**不得出現新增 / 編輯入口**（後端無 POST/PUT，實測 405）；
 *  - 刪除前一律先打 dry_run=1（MappingDeleteDialog 內），正式刪除需填 reason + 二次確認。
 */
export default {
  name: 'ManualFixMappingPage',
  components: {
    MappingDetailDrawer,
    MappingDeleteDialog,
    PosIdPagination,
    PosIdSortableHeader
  },
  data() {
    return {
      loading: false,
      readOnly: false,
      errorText: '',
      total: 0,
      rows: [],
      circuitOptions: [],
      detailVisible: false,
      detailId: null,
      deleteVisible: false,
      deleteRecord: null,
      filters: createDefaultFilters(),
      tableFields: [
        {
          key: 'from_circuit_id',
          label: '來源院線',
          sortKey: 'from_circuit_id',
          thStyle: { width: '90px' }
        },
        {
          key: 'from_pos_id',
          label: '來源pos-id',
          sortKey: 'from_pos_id',
          thStyle: { width: '150px' }
        },
        { key: 'from_title_name', label: '來源標題' },
        { key: 'engagements', label: '舊pos-id剩餘記錄', thStyle: { width: '160px' } },
        {
          key: 'to_record',
          label: '目標pos-id',
          sortKey: 'to_pos_id',
          thStyle: { width: '150px' }
        },
        {
          key: 'created_at',
          label: '建立時間',
          sortKey: 'created_at',
          thStyle: { width: '150px' }
        },
        { key: 'actions', label: '操作', thStyle: { width: '140px' } }
      ],
      posIdModeOptions: POS_ID_MODE_OPTIONS,
      readOnlyHint: POS_ID_READ_ONLY_HINT
    }
  },
  computed: {
    // 當前排序列的表頭以 variant=info 高亮（配合 PosIdSortableHeader 的 ↑ / ↓ 標誌）
    displayFields() {
      return this.tableFields.map((field) => {
        if (field.sortKey && field.sortKey === this.filters.sort) {
          return Object.assign({}, field, { variant: 'info' })
        }
        return field
      })
    }
  },
  created() {
    this.applyRouteQuery()
    this.loadCircuitOptions()
    this.fetchList()
  },
  methods: {
    formatNumber,
    textOrDash,
    /** 深鏈支援：from_pos_id / from_pos_id_mode / to_pos_id / to_pos_id_mode / from_circuit_id */
    applyRouteQuery() {
      const query = (this.$route && this.$route.query) || {}
      if (hasText(query.from_pos_id)) {
        this.filters.from_pos_id = String(query.from_pos_id).trim()
        if (query.from_pos_id_mode === 'exact') {
          this.filters.from_pos_id_mode = 'exact'
        }
      }
      if (hasText(query.to_pos_id)) {
        this.filters.to_pos_id = String(query.to_pos_id).trim()
        if (query.to_pos_id_mode === 'exact') {
          this.filters.to_pos_id_mode = 'exact'
        }
      }
      if (hasText(query.from_circuit_id)) {
        this.filters.from_circuit_id = String(query.from_circuit_id)
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '')
      }
    },
    loadCircuitOptions() {
      getCircuitList()
        .then((res) => {
          this.circuitOptions = extractListPayload(res)
            .filter((item) => item && hasText(item.circuit_key))
            .map((item) => {
              const key = String(item.circuit_key)
              const name = hasText(item.circuit_name) ? String(item.circuit_name) : ''
              return { value: key, text: name === '' ? key : `${key} - ${name}` }
            })
        })
        .catch(() => {
          // 院線下拉只是輔助條件：載入失敗不影響列表（仍可用舊號 / 新號 / 日期查詢）
          this.circuitOptions = []
        })
    },
    fetchList() {
      this.loading = true
      const params = buildManualFixMappingQuery(this.filters)
      getManualFixMappings(params)
        .then((res) => {
          this.rows = extractListPayload(res)
          this.total = res && typeof res.total === 'number' ? res.total : 0
          this.errorText = ''
          this.loading = false
        })
        .catch((error) => {
          const info = resolvePosIdError(error)
          this.rows = []
          this.total = 0
          this.loading = false
          if (info.isPermissionDenied) {
            this.setReadOnly()
          } else {
            this.errorText = info.message
            Message.error(info.message)
          }
        })
    },
    handleSearch() {
      this.filters.page = 1
      this.fetchList()
    },
    handleReset() {
      this.filters = createDefaultFilters()
      this.errorText = ''
      this.fetchList()
    },
    handlePageChange(page) {
      this.filters.page = Number(page) || 1
      this.fetchList()
    },
    handleCountChange(count) {
      this.filters.count = Number(count) || 20
      this.filters.page = 1
      this.fetchList()
    },
    // 表頭點擊排序：同一欄切換升降；改點另一欄時回到預設「新 → 舊」
    handleHeaderSort(sortKey) {
      if (!sortKey) {
        return
      }
      if (this.filters.sort === sortKey) {
        this.filters.direct = this.filters.direct === 'asc' ? 'desc' : 'asc'
      } else {
        this.filters.sort = sortKey
        this.filters.direct = 'desc'
      }
      this.filters.page = 1
      this.fetchList()
    },
    /** 舊號剩餘票房：統一走 mappingEngagementSummary，不在頁面重算 */
    engagementOf(row) {
      return mappingEngagementSummary(row ? row.from_engagements : null)
    },
    hasEngagement(row) {
      return this.engagementOf(row).finalsCount > 0
    },
    engagementCountText(row) {
      return `${formatNumber(this.engagementOf(row).finalsCount)} 條`
    },
    engagementClass(row) {
      return this.hasEngagement(row) ? 'text-danger font-weight-bold' : 'text-muted'
    },
    deadMapping(row) {
      return isDeadMapping(row)
    },
    fromLedgerAlive(row) {
      return hasFromLedger(row)
    },
    toRecordPosId(row) {
      const record = row ? row.to_record : null
      return record ? textOrDash(record.pos_id) : '已不存在（死規則）'
    },
    openDetail(row) {
      this.detailId = row.id
      this.detailVisible = true
    },
    openDelete(row) {
      if (this.readOnly) {
        Message.warning(this.readOnlyHint)
        return
      }
      this.deleteRecord = row
      this.deleteVisible = true
    },
    onDeleted() {
      Message.success('重定向映射已刪除，列表已重新整理')
      this.fetchList()
    },
    // 10010（記錄不存在，含已被刪過）：提示後關閉彈窗並刷新列表（guide §5.3）
    onMissing() {
      this.detailVisible = false
      this.deleteVisible = false
      this.errorText = ''
      this.fetchList()
    },
    setReadOnly() {
      if (!this.readOnly) {
        this.readOnly = true
        this.deleteVisible = false
        Message.warning(this.readOnlyHint)
      }
    },
    goManagementCenter() {
      this.$router.push({ name: 'management-center' })
    },
    /** 刪除後的唯一痕跡是 action=重定向 / change_type=刪除 的變更記錄（guide §5.3） */
    goDeleteAudits() {
      this.$router.push({
        name: 'pos-id-audits',
        query: { change_type: 'delete' }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep table {
  table-layout: fixed;
}
::v-deep td,
::v-deep th {
  white-space: normal;
  word-break: break-word;
  vertical-align: top;
}
</style>
